import React, { useEffect, useRef } from 'react';
import { gsap, ScrollTrigger, prefersReducedMotion } from '../../utils/gsap';

/**
 * BuilderChapter — Visual Memory Workbench (250vh pinned scene)
 *
 * Scroll drives a visual sequence on a dark canvas:
 * 0–15%:  "BEFORE SOFTWARE" annotation appears
 * 15–40%: SVG airplane outline draws via stroke-dashoffset
 * 40–55%: Airplane flies horizontally across the viewport
 * 55–70%: Hydraulic components assemble (syringe + pipe SVGs)
 * 70–85%: Electrolysis diagram animates
 * 85–100%: Closing statement: "I didn't know how to code yet."
 */
const BuilderChapter = () => {
  const wrapperRef = useRef(null);
  const sceneRef = useRef(null);
  const annotationRef = useRef(null);
  const airplaneRef = useRef(null);
  const airplanePathRef = useRef(null);
  const hydraulicRef = useRef(null);
  const electrolysisRef = useRef(null);
  const closingRef = useRef(null);
  const quoteRef = useRef(null);
  const metaRef = useRef(null);
  const glowRef = useRef(null);
  const explanatoryRef = useRef(null);
  const contrailRef = useRef(null);

  useEffect(() => {
    if (prefersReducedMotion()) return;

    let ctx;
    const setup = () => {
      // Set initial stroke-dasharray for the airplane path
      if (airplanePathRef.current) {
        const len = airplanePathRef.current.getTotalLength?.() || 300;
        gsap.set(airplanePathRef.current, {
          strokeDasharray: len,
          strokeDashoffset: len,
        });
      }

        // Explicitly hide quote elements initially until GSAP timeline reaches closing reveal
        if (quoteRef.current && metaRef.current && glowRef.current && explanatoryRef.current) {
          gsap.set([quoteRef.current, metaRef.current, glowRef.current, explanatoryRef.current], { opacity: 0 });
        }

        ctx = gsap.context(() => {
          const tl = gsap.timeline({
            scrollTrigger: {
              trigger: wrapperRef.current,
              start: 'top top',
              end: '+=230%',
              pin: sceneRef.current,
              scrub: 1.2,
              anticipatePin: 1,
            }
          });

        // 0–30%: Closing statement cinematic reveal right as section opens
        tl.fromTo(annotationRef.current,
          { opacity: 0, x: -20 },
          { opacity: 1, x: 0, ease: 'power2.out', duration: 0.4 },
          0
        )
        .fromTo(glowRef.current,
          { opacity: 0, scale: 0.6 },
          { opacity: 0.25, scale: 1, ease: 'power2.out', duration: 1.2 },
          0.1
        )
        .fromTo(quoteRef.current,
          { opacity: 0, y: 55, scale: 0.92, filter: 'blur(12px)' },
          { opacity: 1, y: 0, scale: 1, filter: 'blur(0px)', ease: 'power3.out', duration: 1.3 },
          0.2
        )
        .fromTo(metaRef.current,
          { opacity: 0, y: 20, letterSpacing: '0.38em' },
          { opacity: 1, y: 0, letterSpacing: '0.2em', ease: 'power2.out', duration: 0.9 },
          0.6
        )

        // 30–50%: Quote fades out as workbench diagrams assemble below
        .to([quoteRef.current, metaRef.current, glowRef.current],
          { opacity: 0, y: -30, duration: 0.6 },
          1.8
        )

        // 50–70%: Workbench SVG airplane draws & flies
        .to(airplanePathRef.current, {
          strokeDashoffset: 0,
          ease: 'power1.inOut',
          duration: 1.2
        }, 2.4)
        .fromTo(airplaneRef.current,
          { opacity: 0 },
          { opacity: 1, ease: 'none', duration: 0.2 },
          2.4
        )
        .to(airplaneRef.current, {
          x: '125vw',
          opacity: 0,
          ease: 'power1.in',
          duration: 1.0
        }, 3.2)
        .fromTo(contrailRef.current,
          { width: 0, opacity: 0.7 },
          { width: '120vw', opacity: 0, ease: 'power1.out', duration: 1.0 },
          3.2
        )

        // 70–100%: hydraulic components, electrolysis diagram, & explanatory text reveal together
        .fromTo(hydraulicRef.current,
          { opacity: 0, scale: 0.85, y: 30 },
          { opacity: 1, scale: 1, y: 0, ease: 'back.out(1.5)', duration: 0.8 },
          4.0
        )
        .fromTo(hydraulicRef.current.querySelectorAll('.hyd-part'),
          { opacity: 0, y: 15 },
          { opacity: 1, y: 0, stagger: 0.12, ease: 'power2.out', duration: 0.5 },
          4.2
        )
        .fromTo(electrolysisRef.current,
          { opacity: 0, scale: 0.9 },
          { opacity: 1, scale: 1, ease: 'power2.out', duration: 0.7 },
          4.4
        )
        .fromTo(explanatoryRef.current,
          { opacity: 0, y: 25 },
          { opacity: 1, y: 0, ease: 'power3.out', duration: 0.9 },
          4.4
        );
      });
    };

    ScrollTrigger.refresh();
    setup();
    return () => { if (ctx) ctx.revert(); };
  }, []);

  return (
    <section ref={wrapperRef} className="builder-scroll-wrapper">
      <div ref={sceneRef} className="builder-scene">

        {/* Background grid lines */}
        <div className="builder-grid-bg" aria-hidden="true" />

        {/* Annotation label */}
        <div ref={annotationRef} className="builder-annotation font-mono">
          CH. 01 &nbsp;// BEFORE SOFTWARE
        </div>

        {/* Airplane SVG */}
        <div ref={airplaneRef} className="airplane-container" aria-hidden="true">
          {/* Contrail line */}
          <div ref={contrailRef} className="airplane-contrail" />
          <svg
            viewBox="0 0 220 80"
            width="180"
            height="65"
            fill="none"
            className="airplane-svg"
          >
            {/* Airplane body */}
            <path
              ref={airplanePathRef}
              d="M10 40 L60 35 L120 20 L170 30 L200 40 L170 50 L120 60 L60 45 Z
                 M60 38 L60 25 L90 15 L100 35
                 M170 40 L190 30 L195 40 L190 50 Z
                 M60 42 L60 55 L80 62 L90 50"
              stroke="rgba(212,163,89,0.8)"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>

        {/* Centered Workbench Diagrams with Explanatory Text */}
        <div className="builder-diagrams-group">
          <div className="builder-diagrams-row">
            {/* Hydraulic assembly card */}
            <div ref={hydraulicRef} className="workbench-card hydraulic-assembly">
              <div className="card-header font-mono">
                <span className="card-dot gold-dot" />
                <span className="card-title">HYDRAULIC LIFT</span>
                <span className="card-badge font-mono">PASQUAL LOGIC</span>
              </div>
              <div className="card-canvas">
                <svg viewBox="0 0 300 180" width="100%" height="180" fill="none">
                  <defs>
                    <linearGradient id="hyd-piston-grad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#d4a359" stopOpacity="0.9" />
                      <stop offset="100%" stopColor="#d4a359" stopOpacity="0.3" />
                    </linearGradient>
                    <linearGradient id="hyd-fluid-grad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#38bdf8" stopOpacity="0.4" />
                      <stop offset="100%" stopColor="#38bdf8" stopOpacity="0.1" />
                    </linearGradient>
                    <pattern id="card-grid" width="15" height="15" patternUnits="userSpaceOnUse">
                      <path d="M 15 0 L 0 0 0 15" fill="none" stroke="rgba(255,255,255,0.02)" strokeWidth="1" />
                    </pattern>
                  </defs>

                  {/* Canvas Grid */}
                  <rect width="300" height="180" fill="url(#card-grid)" rx="6" />

                  {/* Top Heavy Load Platform */}
                  <rect className="hyd-part" x="50" y="16" width="200" height="12" rx="3"
                    fill="rgba(212,163,89,0.12)" stroke="rgba(212,163,89,0.85)" strokeWidth="1.5" />
                  <path className="hyd-part" d="M 55 22 L 245 22" stroke="rgba(212,163,89,0.4)" strokeWidth="1" strokeDasharray="3 3" />

                  {/* Vertical Piston Shaft */}
                  <rect className="hyd-part" x="142" y="28" width="16" height="60" rx="2"
                    fill="url(#hyd-piston-grad)" stroke="#d4a359" strokeWidth="1.5" />

                  {/* Main Cylinder Base */}
                  <rect className="hyd-part" x="124" y="80" width="52" height="65" rx="5"
                    fill="rgba(15, 23, 42, 0.8)" stroke="rgba(56,189,248,0.85)" strokeWidth="1.5" />
                  <rect className="hyd-part" x="127" y="105" width="46" height="37" rx="3"
                    fill="url(#hyd-fluid-grad)" stroke="rgba(56,189,248,0.4)" strokeWidth="1" />
                  
                  {/* Cylinder Seal Line */}
                  <line className="hyd-part" x1="124" y1="95" x2="176" y2="95" stroke="rgba(212,163,89,0.7)" strokeWidth="2" />

                  {/* Left Syringe Actuator */}
                  <rect className="hyd-part" x="20" y="100" width="55" height="22" rx="3"
                    fill="rgba(15, 23, 42, 0.8)" stroke="rgba(56,189,248,0.8)" strokeWidth="1.5" />
                  <rect className="hyd-part" x="32" y="102" width="41" height="18" fill="url(#hyd-fluid-grad)" />
                  <path className="hyd-part" d="M 5 111 L 20 111 M 5 104 L 5 118" stroke="#d4a359" strokeWidth="1.5" strokeLinecap="round" />
                  <rect className="hyd-part" x="21" y="102" width="10" height="18" rx="1" fill="rgba(212,163,89,0.5)" />
                  {/* Left Curved Tube */}
                  <path className="hyd-part" d="M 75 111 C 95 111, 105 130, 124 130"
                    stroke="#38bdf8" strokeWidth="2.2" fill="none" strokeLinecap="round" />

                  {/* Right Syringe Actuator */}
                  <rect className="hyd-part" x="225" y="100" width="55" height="22" rx="3"
                    fill="rgba(15, 23, 42, 0.8)" stroke="rgba(56,189,248,0.8)" strokeWidth="1.5" />
                  <rect className="hyd-part" x="227" y="102" width="41" height="18" fill="url(#hyd-fluid-grad)" />
                  <path className="hyd-part" d="M 280 111 L 295 111 M 295 104 L 295 118" stroke="#d4a359" strokeWidth="1.5" strokeLinecap="round" />
                  <rect className="hyd-part" x="267" y="102" width="10" height="18" rx="1" fill="rgba(212,163,89,0.5)" />
                  {/* Right Curved Tube */}
                  <path className="hyd-part" d="M 225 111 C 205 111, 195 130, 176 130"
                    stroke="#38bdf8" strokeWidth="2.2" fill="none" strokeLinecap="round" />

                  {/* Measurement Annotations */}
                  <text className="hyd-part" x="150" y="162" fill="rgba(212,163,89,0.7)"
                    fontSize="8.5" fontFamily="monospace" textAnchor="middle" letterSpacing="1.2">
                    SYRINGE • TUBE • HYDRAULIC PRESSURE
                  </text>
                </svg>
              </div>
            </div>

            {/* Electrolysis diagram card */}
            <div ref={electrolysisRef} className="workbench-card electrolysis-assembly">
              <div className="card-header font-mono">
                <span className="card-dot cyan-dot" />
                <span className="card-title">ELECTROLYSIS SETUP</span>
                <span className="card-badge font-mono">H₂ + O₂ SPLIT</span>
              </div>
              <div className="card-canvas">
                <svg viewBox="0 0 300 180" width="100%" height="180" fill="none">
                  <defs>
                    <linearGradient id="elec-water-grad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#38bdf8" stopOpacity="0.25" />
                      <stop offset="100%" stopColor="#38bdf8" stopOpacity="0.06" />
                    </linearGradient>
                  </defs>

                  {/* Canvas Grid */}
                  <rect width="300" height="180" fill="url(#card-grid)" rx="6" />

                  {/* 9V Battery Top Block */}
                  <rect x="125" y="8" width="50" height="22" rx="4"
                    fill="rgba(15, 23, 42, 0.95)" stroke="rgba(212,163,89,0.85)" strokeWidth="1.5" />
                  <rect x="135" y="4" width="8" height="4" rx="1" fill="#d4a359" />
                  <rect x="157" y="4" width="8" height="4" rx="1" fill="#38bdf8" />
                  <text x="150" y="23" fill="rgba(212,163,89,0.9)" fontSize="8" fontFamily="monospace" textAnchor="middle" fontWeight="bold">9V BATTERY</text>

                  {/* Connecting Wires */}
                  <path d="M 139 28 C 139 42, 108 34, 108 50" stroke="#d4a359" strokeWidth="1.8" fill="none" strokeDasharray="4 2" />
                  <path d="M 161 28 C 161 42, 192 34, 192 50" stroke="#38bdf8" strokeWidth="1.8" fill="none" strokeDasharray="4 2" />

                  {/* Glass Beaker */}
                  <path d="M 70 42 L 70 142 Q 70 156 86 156 L 214 156 Q 230 156 230 142 L 230 42"
                    stroke="rgba(56,189,248,0.7)" strokeWidth="1.8" fill="rgba(15, 23, 42, 0.7)" />
                  <path d="M 64 42 L 236 42" stroke="rgba(56,189,248,0.8)" strokeWidth="2" strokeLinecap="round" />

                  {/* Water Fill */}
                  <path d="M 71 88 Q 150 84 229 88 L 229 142 Q 229 155 214 155 L 86 155 Q 71 155 71 142 Z"
                    fill="url(#elec-water-grad)" />
                  <path d="M 71 88 Q 150 84 229 88" stroke="rgba(56,189,248,0.5)" strokeWidth="1" strokeDasharray="4 3" fill="none" />

                  {/* Cathode (Negative Electrode - Gold) */}
                  <rect x="101" y="50" width="14" height="88" rx="3"
                    fill="rgba(212,163,89,0.18)" stroke="#d4a359" strokeWidth="1.5" />
                  <text x="108" y="148" fill="#d4a359" fontSize="8.5" fontFamily="monospace" textAnchor="middle" fontWeight="bold">– H₂</text>

                  {/* Anode (Positive Electrode - Cyan) */}
                  <rect x="185" y="50" width="14" height="88" rx="3"
                    fill="rgba(56,189,248,0.18)" stroke="#38bdf8" strokeWidth="1.5" />
                  <text x="192" y="148" fill="#38bdf8" fontSize="8.5" fontFamily="monospace" textAnchor="middle" fontWeight="bold">+ O₂</text>

                  {/* Rising Gas Bubbles */}
                  <circle className="elec-bubble" cx="104" cy="118" r="3" fill="rgba(212,163,89,0.7)" />
                  <circle className="elec-bubble" cx="111" cy="104" r="4" fill="rgba(212,163,89,0.8)" />
                  <circle className="elec-bubble" cx="106" cy="88" r="3.5" fill="rgba(212,163,89,0.6)" />
                  <circle className="elec-bubble" cx="112" cy="74" r="4.5" fill="rgba(212,163,89,0.9)" />

                  <circle className="elec-bubble" cx="188" cy="114" r="3" fill="rgba(56,189,248,0.7)" />
                  <circle className="elec-bubble" cx="194" cy="98" r="4" fill="rgba(56,189,248,0.8)" />
                  <circle className="elec-bubble" cx="189" cy="82" r="3.5" fill="rgba(56,189,248,0.9)" />

                  {/* Subtext */}
                  <text x="150" y="166" fill="rgba(212,163,89,0.7)"
                    fontSize="8.5" fontFamily="monospace" textAnchor="middle" letterSpacing="1.2">
                    WATER ELECTROLYSIS • CHEMICAL REACTION
                  </text>
                </svg>
              </div>
            </div>
          </div>

          {/* Explanatory Narrative Text Block */}
          <div ref={explanatoryRef} className="builder-explanatory-block font-mono">
            <div className="builder-tag">
              <span className="tag-dot" />
              PHYSICAL WORKSHOP &nbsp;•&nbsp; LAHORE, PAKISTAN
            </div>
            <p className="builder-desc">
              Before writing code, software was physical logic: building hydraulic lifts with syringe pressure, 
              splitting water into hydrogen with 9V batteries, and folding aerodynamic gliders.
            </p>
          </div>
        </div>

        {/* Closing statement with radial gold glow */}
        <div ref={closingRef} className="builder-closing">
          <div ref={glowRef} className="builder-quote-glow" aria-hidden="true" />
          <blockquote ref={quoteRef} className="builder-closing-quote font-display">
            "I didn't know how to code yet.<br />
            I just knew I liked making things work."
          </blockquote>
          <div ref={metaRef} className="builder-closing-meta font-mono">
            BEFORE THE CODE &nbsp;—&nbsp; LAHORE, PAKISTAN
          </div>
        </div>
      </div>

      <style>{`
        .builder-scroll-wrapper {
          position: relative;
          height: 320vh;
          background: #080a0f;
        }

        .builder-scene {
          position: relative;
          width: 100%;
          height: 100vh;
          overflow: hidden;
          background: #080a0f;
          display: flex;
          align-items: center;
          justify-content: center;
          will-change: transform;
        }

        .builder-grid-bg {
          position: absolute;
          inset: 0;
          background-image:
            linear-gradient(rgba(56, 189, 248, 0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(56, 189, 248, 0.03) 1px, transparent 1px);
          background-size: 60px 60px;
          pointer-events: none;
        }

        .builder-annotation {
          position: absolute;
          top: 8vh;
          left: 5vw;
          font-size: 0.68rem;
          letter-spacing: 0.2em;
          color: rgba(212, 163, 89, 0.6);
          will-change: opacity, transform;
        }

        .airplane-container {
          position: absolute;
          left: -10vw;
          top: 50%;
          transform: translateY(-50%);
          display: flex;
          align-items: center;
          will-change: transform, opacity;
        }

        .airplane-contrail {
          position: absolute;
          left: -90vw;
          top: 50%;
          transform: translateY(-50%);
          height: 1px;
          background: linear-gradient(to right, transparent, rgba(212,163,89,0.4));
          will-change: width, opacity;
        }

        .airplane-svg {
          filter: drop-shadow(0 0 8px rgba(212,163,89,0.3));
        }

        .builder-diagrams-group {
          position: absolute;
          left: 50%;
          top: 50%;
          transform: translate(-50%, -50%);
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 1.8rem;
          width: 100%;
          max-width: 900px;
          z-index: 10;
          padding: 0 1rem;
        }

        .builder-diagrams-row {
          display: flex;
          align-items: stretch;
          justify-content: center;
          gap: 2rem;
          width: 100%;
        }

        .workbench-card {
          flex: 1;
          min-width: 320px;
          max-width: 420px;
          background: rgba(13, 18, 30, 0.65);
          backdrop-filter: blur(16px);
          border: 1px solid rgba(212, 163, 89, 0.18);
          border-radius: 12px;
          padding: 1rem 1.25rem 1.25rem 1.25rem;
          box-shadow:
            0 12px 32px rgba(0, 0, 0, 0.5),
            inset 0 1px 0 rgba(255, 255, 255, 0.05);
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
          transition: border-color 0.3s ease, box-shadow 0.3s ease;
        }

        .workbench-card:hover {
          border-color: rgba(212, 163, 89, 0.35);
          box-shadow:
            0 16px 40px rgba(0, 0, 0, 0.6),
            0 0 20px rgba(212, 163, 89, 0.08);
        }

        .card-header {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding-bottom: 0.65rem;
          border-bottom: 1px solid rgba(255, 255, 255, 0.06);
        }

        .card-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
        }

        .gold-dot {
          background: #d4a359;
          box-shadow: 0 0 8px #d4a359;
        }

        .cyan-dot {
          background: #38bdf8;
          box-shadow: 0 0 8px #38bdf8;
        }

        .card-title {
          font-size: 0.7rem;
          font-weight: 700;
          letter-spacing: 0.15em;
          color: #f3f4f6;
        }

        .card-badge {
          margin-left: auto;
          font-size: 0.58rem;
          letter-spacing: 0.1em;
          color: rgba(212, 163, 89, 0.75);
          background: rgba(212, 163, 89, 0.08);
          border: 1px solid rgba(212, 163, 89, 0.2);
          padding: 0.15rem 0.5rem;
          border-radius: 4px;
        }

        .card-canvas {
          width: 100%;
          border-radius: 6px;
          overflow: hidden;
          background: rgba(8, 10, 15, 0.5);
          border: 1px solid rgba(255, 255, 255, 0.03);
        }

        .builder-explanatory-block {
          text-align: center;
          max-width: 580px;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.75rem;
          opacity: 0;
          will-change: opacity, transform;
        }

        .builder-tag {
          font-size: 0.65rem;
          letter-spacing: 0.2em;
          color: var(--accent-gold);
          display: flex;
          align-items: center;
          gap: 0.5rem;
          background: rgba(212, 163, 89, 0.08);
          border: 1px solid rgba(212, 163, 89, 0.25);
          padding: 0.35rem 0.85rem;
          border-radius: 100px;
        }

        .tag-dot {
          width: 5px;
          height: 5px;
          border-radius: 50%;
          background: var(--accent-gold);
          box-shadow: 0 0 8px var(--accent-gold);
        }

        .builder-desc {
          font-size: clamp(0.78rem, 1.1vw, 0.92rem);
          line-height: 1.6;
          color: rgba(209, 213, 219, 0.78);
          margin: 0;
        }

        .hydraulic-assembly,
        .electrolysis-assembly {
          position: relative;
          will-change: opacity, transform;
        }

        .builder-closing {
          position: absolute;
          inset: 0;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          padding: 3rem;
          will-change: opacity, transform;
        }

        .builder-quote-glow {
          position: absolute;
          width: 550px;
          height: 320px;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(212, 163, 89, 0.22) 0%, transparent 70%);
          pointer-events: none;
          z-index: -1;
          opacity: 0;
          will-change: opacity, transform;
        }

        .builder-closing-quote {
          font-size: clamp(1.6rem, 3.5vw, 2.8rem);
          line-height: 1.4;
          color: #f3f4f6;
          font-style: italic;
          font-family: 'Playfair Display', Georgia, serif;
          font-weight: 400;
          max-width: 720px;
          border: none;
          padding: 0;
          margin-bottom: 1.5rem;
          opacity: 0;
          will-change: opacity, transform, filter;
        }

        .builder-closing-meta {
          font-size: 0.68rem;
          letter-spacing: 0.2em;
          color: rgba(212, 163, 89, 0.7);
          opacity: 0;
          will-change: opacity, transform, letter-spacing;
        }

        @media (max-width: 768px) {
          .builder-scroll-wrapper {
            height: 300vh;
          }

          .builder-annotation {
            top: calc(56px + 12px);
            left: 5vw;
            font-size: 0.62rem;
            letter-spacing: 0.16em;
          }

          .airplane-container {
            display: none !important;
          }

          .builder-scroll-wrapper,
          .builder-scene {
            overflow: hidden !important;
            max-width: 100vw;
          }

          /* Diagrams — perfectly proportioned for mobile viewport */
          .builder-diagrams-group {
            position: absolute;
            left: 50% !important;
            top: calc(50% + 14px) !important;
            transform: translate(-50%, -50%) !important;
            flex-direction: column !important;
            gap: 0.65rem !important;
            align-items: center;
            width: 92vw;
            max-width: 380px;
            padding: 0;
          }

          .builder-diagrams-row {
            flex-direction: column;
            gap: 0.55rem;
            width: 100%;
          }

          .workbench-card {
            min-width: unset;
            max-width: 100%;
            width: 100%;
            padding: 0.55rem 0.75rem 0.65rem;
            gap: 0.35rem;
            border-radius: 10px;
            transform: none !important;
          }

          .card-header {
            padding-bottom: 0.3rem;
          }

          .card-title {
            font-size: 0.62rem;
            letter-spacing: 0.1em;
          }

          .card-badge {
            font-size: 0.52rem;
            padding: 0.1rem 0.4rem;
          }

          .card-canvas svg {
            height: 95px !important;
          }

          /* Explanatory text — compact & readable */
          .builder-explanatory-block {
            width: 90vw;
            max-width: 380px;
            padding: 0;
            text-align: center;
            gap: 0.4rem;
          }

          .builder-tag {
            font-size: 0.56rem;
            letter-spacing: 0.14em;
            padding: 0.25rem 0.65rem;
          }

          .builder-desc {
            font-size: 0.76rem;
            line-height: 1.45;
          }

          /* Closing Quote on mobile */
          .builder-closing {
            padding: 1.5rem;
          }

          .builder-closing-quote {
            font-size: clamp(1.25rem, 5.5vw, 1.75rem);
            width: 88vw;
            text-align: center;
            line-height: 1.4;
            margin-bottom: 1rem;
          }

          .builder-closing-meta {
            font-size: 0.58rem;
            letter-spacing: 0.16em;
          }

          .builder-quote-glow {
            width: 90vw;
            height: 240px;
          }
        }
      `}</style>
    </section>
  );
};

export default BuilderChapter;
